import { FormEvent, useEffect, useState } from 'react';
import type { ExpenseItem, FinanceAccount, MoneyAccountKind, OutletRef } from '@kassa/shared';
import { api, ApiError } from '../api';
import { DataTable, PageHeader, type Column } from '../components/ListPage';

// Maliyyə → «Hesablar» (KT-102): kassa və bank hesabları (obyekt üzrə və ya şirkət), qalıq yalnız ödənişlərdən; xərc maddələri.
// Kassa (növbə) hesabları kassalarla avtomatik yaranır; ofis nağd hesabı növbəsizdir.

const KIND: Record<FinanceAccount['kind'], string> = { cash: 'Kassa (nağd)', bank: 'Bank', card_terminal: 'Kart terminalı' };

function errText(err: unknown): string {
  return err instanceof ApiError ? err.message : 'Şəbəkə xətası. Yenidən cəhd edin.';
}

function money(s: string): string {
  const [i, f = '00'] = s.replace('-', '').split('.');
  return `${s.startsWith('-') ? '−' : ''}${i.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')},${f.padEnd(2, '0')} ₼`;
}

export function FinanceAccounts() {
  const [rows, setRows] = useState<FinanceAccount[] | null>(null);
  const [items, setItems] = useState<ExpenseItem[]>([]);
  const [outlets, setOutlets] = useState<OutletRef[]>([]);
  const [form, setForm] = useState<{ kind: MoneyAccountKind; name: string; outletId: string; bankAccount: string } | null>(null);
  const [itemName, setItemName] = useState('');
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.financeAccounts().then(setRows).catch((err) => setMsg({ ok: false, text: errText(err) }));
    api.expenseItems().then(setItems).catch(() => setItems([]));
  };
  useEffect(() => {
    load();
    api.outlets().then(setOutlets).catch(() => setOutlets([]));
  }, []);

  async function run(fn: () => Promise<string>) {
    setBusy(true);
    setMsg(null);
    try {
      setMsg({ ok: true, text: await fn() });
      load();
    } catch (err) {
      setMsg({ ok: false, text: errText(err) });
    } finally {
      setBusy(false);
    }
  }

  function save(e: FormEvent) {
    e.preventDefault();
    if (!form) return;
    void run(async () => {
      await api.financeAccountCreate({ kind: form.kind, name: form.name, outletId: form.outletId || null, bankAccount: form.bankAccount || null });
      setForm(null);
      return 'Hesab yaradıldı.';
    });
  }

  const columns: Column<FinanceAccount>[] = [
    { key: 'name', label: 'Hesab', render: (a) => (<>{a.name}{a.registerName && <div className="muted small">Kassa: {a.registerName} (növbə ilə)</div>}{a.bankAccount && <div className="muted small mono">{a.bankAccount}</div>}</>) },
    { key: 'kind', label: 'Növ', render: (a) => KIND[a.kind] },
    { key: 'outlet', label: 'Obyekt', minor: true, render: (a) => a.outletName ?? 'Şirkət' },
    { key: 'bal', label: 'Qalıq', align: 'right', mono: true, render: (a) => <span className={a.balance.startsWith('-') ? 'bad' : undefined}>{money(a.balance)}</span> },
    { key: 'st', label: 'Status', minor: true, render: (a) => (a.active ? <span className="badge badge-ok">Aktiv</span> : <span className="badge badge-off">Bağlı</span>) },
  ];

  return (
    <main className="page page-list">
      <PageHeader
        title="Hesablar"
        actions={
          !form && (
            <button type="button" className="btn-primary btn-small" onClick={() => setForm({ kind: 'bank', name: '', outletId: '', bankAccount: '' })}>
              + Yeni hesab
            </button>
          )
        }
      />
      {msg && <p className={msg.ok ? 'ok' : 'bad'} role="status">{msg.text}</p>}
      {form && (
        <form className="card form-stack" onSubmit={save} noValidate aria-label="Yeni hesab">
          <div className="form-grid">
            <label>
              Növ
              <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value as MoneyAccountKind })} disabled={busy}>
                <option value="bank">Bank</option>
                <option value="cash">Ofis kassası (nağd, növbəsiz)</option>
              </select>
            </label>
            <label>
              Ad
              <input value={form.name} maxLength={120} onChange={(e) => setForm({ ...form, name: e.target.value })} disabled={busy} />
            </label>
            <label>
              Obyekt
              <select value={form.outletId} onChange={(e) => setForm({ ...form, outletId: e.target.value })} disabled={busy}>
                <option value="">Şirkət (bütün obyektlər)</option>
                {outlets.map((o) => (
                  <option key={o.id} value={o.id}>{o.name}</option>
                ))}
              </select>
            </label>
            {form.kind === 'bank' && (
              <label>
                Hesab nömrəsi (IBAN)
                <input value={form.bankAccount} maxLength={40} onChange={(e) => setForm({ ...form, bankAccount: e.target.value })} disabled={busy} />
              </label>
            )}
          </div>
          <div className="form-actions">
            <button type="submit" className="btn-primary btn-small" disabled={busy || !form.name.trim()}>Saxla</button>
            <button type="button" className="btn-small" onClick={() => setForm(null)} disabled={busy}>Ləğv et</button>
          </div>
        </form>
      )}
      <DataTable label="Hesablar" columns={columns} rows={rows ?? []} rowKey={(a) => a.id} loading={rows === null && !msg} error={null} empty="Hesab yoxdur." />

      <section className="card" aria-labelledby="xerc-title">
        <h2 id="xerc-title">Xərc maddələri</h2>
        <form
          className="list-bar"
          onSubmit={(e) => {
            e.preventDefault();
            if (!itemName.trim()) return;
            void run(async () => {
              await api.expenseItemCreate(itemName.trim());
              setItemName('');
              return 'Xərc maddəsi əlavə olundu.';
            });
          }}
        >
          <label className="sr-only" htmlFor="xerc-ad">Yeni xərc maddəsi</label>
          <input id="xerc-ad" value={itemName} maxLength={120} placeholder="Məs. Nəqliyyat, İcarə, Kommunal" onChange={(e) => setItemName(e.target.value)} disabled={busy} />
          <button type="submit" className="btn-small" disabled={busy || !itemName.trim()}>Əlavə et</button>
        </form>
        {items.length === 0 ? <p className="muted">Hələ xərc maddəsi yoxdur.</p> : (
          <ul className="plain-list">
            {items.map((i) => (
              <li key={i.id}>{i.name}{!i.active && <span className="muted"> (bağlı)</span>}</li>
            ))}
          </ul>
        )}
      </section>
    </main>
  );
}
